'use strict'

import 'intersection-observer';

import { loadComponent } from './utils/load-component';
import { format } from 'date-fns';
import { getDigitalEventData, getDigitalPerformances } from '../services/digital-inventory';
import zonedDate from '../utils/zoned-date';
import { getSessionKey } from '../services/user';

const now = zonedDate();

loadComponent(
    'digital-calendar-list',
    {
        props: [
            'calendarItemFocusedClass',
        ],
        data: function () {
            return {
                ViewModel: {
                    LoggedIn: false,
                    Loading: true,
                    Years: [],
                    Results: [],
                }
            };
        },

        beforeMount: function () {
            this.$root.$on('DatePicker.selectedDate', this.dateSelected);
            this.$root.$on('CalendarFilters.filtersUpdated', this.filtersChanged);

            getSessionKey().then(sessionKey => {
                this.ViewModel.LoggedIn = !!sessionKey;

                return getDigitalEventData('video');
            }).then(yearData => {
                this.ViewModel.Years = yearData;
                this.updateFilters();
            }).finally(() => {
                this.ViewModel.Loading = false;
                this.updateFiltersFromUrl();

                window.addEventListener('popstate', () => {
                    this.updateFiltersFromUrl();
                });
            });
        },

        methods: {
            performanceClicked: function (perf) {
                this.$root.$emit('CalendarPopup.scrollToPerformance', perf);
            },
            perfDateId: function (perf) {
                if (perf.PerformanceDate) {
                    return `date-${format(zonedDate(perf.PerformanceDate), 'yyyy-MM-dd')}`;
                }
            },

            updateFilters: function () {
                getDigitalPerformances('video').then(perfs => {
                    perfs = perfs.filter(item => {
                        return zonedDate(item.first_aired) > now;
                    });

                    this.ViewModel.Results = perfs;
                    let filters = [
                        {
                            ID: 'type',
                            Name: 'Type',
                            Options: [],
                        },
                    ];

                    perfs.reduce((filters, perf) => {
                        // Type
                        const typeFilter = filters[0];
                        if (perf.Filters.Type) {
                            perf.Filters.Type.forEach(type => {
                                const opt = typeFilter.Options.find(item => item.ID === type);
                                if (!opt) {
                                    typeFilter.Options.push({
                                        Value: type,
                                        ID: type,
                                        Label: type,
                                        Order: -1,
                                    });
                                }
                            });
                        }

                        perf.keywords.forEach(kw => {
                            const id = kw.type.replace(/[\-\s]/, '_');
                            let filter = filters.find(f => f.ID === id);
                            if (!filter) {
                                filter = {
                                    ID: id,
                                    Name: kw.type,
                                    Options: [],
                                    Order: kw.type_order,
                                };
                                filters.push(filter);
                            }

                            if (!filter.Options.find(o => o.ID === kw.value)) {
                                filter.Options.push({
                                    Value: kw.value,
                                    ID: kw.value,
                                    Label: kw.value,
                                    Order: kw.order
                                });
                            }

                            if (!perf.Filters[id]) {
                                perf.Filters[id] = [];
                            }

                            perf.Filters[id].push(kw.value);
                        });

                        return filters;
                    }, filters);

                    filters.forEach(filter => filter.Options.sort((a, b) => a.Order > b.Order ? 1 : -1));
                    filters.sort((a, b) => a.Order > b.Order ? 1 : -1);

                    this.$root.$emit('CalendarFilter.updateFilters', filters);
                })
            },

            updateFiltersFromUrl: function () {
                const searchParams = new URLSearchParams(window.location.search);

                const filters = {};

                searchParams.forEach((value, key) => {
                    if (key in filters === false) {
                        filters[key] = [];
                    }

                    filters[key].push(value);
                });

                this.filtersChanged(filters, false);
                this.$root.$emit('CalendarFilter.updateAppliedFilters', filters);
            },

            filtersChanged: function (filters, updateUrl = true) {
                if (updateUrl) {
                    const searchParams = new URLSearchParams();

                    for (const filterKey of Object.keys(filters)) {
                        for (const filterValue of filters[filterKey]) {
                            searchParams.append(filterKey, filterValue);
                        }
                    }

                    const url = new URL(window.location);

                    url.search = searchParams.toString();

                    window.history.pushState(null, '', url.toString());
                }

                getDigitalEventData('video')
                    .then(yearData => {
                        this.ViewModel.Years = yearData.reduce((carry, year) => {
                            const months = year.Months.reduce((carry, month) => {
                                const perfs = month.Performances.filter(perf => {
                                    return Object.keys(filters).filter(filterKey => {
                                        if (!filters[filterKey].length) {
                                            // Not in the filter so just include
                                            return true;
                                        }
                                        if (perf.Filters[filterKey]) {
                                            return perf.Filters[filterKey].filter(filterItem => {
                                                return filters[filterKey].indexOf(filterItem.value ?? filterItem) > -1;
                                            }).length > 0;
                                        }
                                    }).length === Object.keys(filters).length;
                                });
                                if (perfs.length) {
                                    carry.push({
                                        Name: month.Name,
                                        Performances: perfs,
                                    })
                                }
                                return carry;
                            }, []);
                            if (months.length) {
                                carry.push({
                                    Name: year.Name,
                                    Months: months,
                                })
                            }
                            return carry;
                        }, []);

                        // Reduces the Performances into a single level array and passes to CalendarPopup
                        this.$root.$emit('CalendarPopup.updatePerformances', this.ViewModel.Years.reduce((carry, year) => {
                            return year.Months.reduce((carry, month) => {
                                return carry.concat(month.Performances);
                            }, carry);
                        }, []));
                        this.updateCalendarDates();
                    })
            },
            updateCalendarDates: function() {
                const newDates = this.ViewModel.Years.reduce((dates, year) => year.Months.reduce((dates, month) => month.Performances.reduce((dates, perf) => {
                        dates.push(format(zonedDate(perf.first_aired), 'yyyy-MM-dd'));
                        return dates;
                    }, dates), dates), []);

                this.$root.$emit(
                    'DatePicker.updateCalendarDates',
                    newDates
                );
            },
            dateSelected: function (dateString) {
                // eslint-disable-next-line
                const observer = new IntersectionObserver(entries => {
                    const [firstEl] = entries;

                    if (firstEl.isIntersecting) {
                        setTimeout(() => {
                            els.forEach(el => el.classList.remove(this.calendarItemFocusedClass));
                        }, 250);
                        observer.disconnect();
                    }
                });
                const els = document.querySelectorAll(`#date-${dateString}`);
                observer.observe(els.item(0));
                els.item(0).scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                els.forEach(el => {
                    el.classList.add(this.calendarItemFocusedClass);
                });
            },
        },
    }
)
